import React, { useState, useContext, useEffect } from 'react';
import i18n from 'i18n-js';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, IconButton, ListItem, ListItemText, styled, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { Card } from 'components/UI';
import { UserContext } from 'context';
import { vehicleActions } from 'state/modules/vehicles';
import VehiclesGroupsLabel from 'components/VehiclesGroups/VehicleGroupsLabel';
import { FETCH_STATE } from 'constants';
import EditAssignedGroupModal from './EditAssignedGroupModal';

const StyledListItem = styled(ListItem)({
  width: '100%',
  padding: '0 5px',
  borderBottom: '1px solid #EEE',
  marginBottom: 10,
});

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  textAlign: 'right',
  margin: 0, // Reset default margin
  '& .MuiListItemText-primary': {
    color: theme.palette.text.primary, // approximate for widgets.text.primary
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'capitalize',
    textAlign: 'left',
    wordBreak: 'break-word',
  },
  '& .MuiListItemText-secondary': {
    color: '#2C2C2E',
    fontSize: 12,
    fontWeight: 400,
    maxWidth: '60%',
    wordBreak: 'break-word',
    '& span:last-child': {
      display: 'block',
      color: theme.palette.text.primary,
      fontSize: 11,
      textTransform: 'capitalize',
    },
  },
}));

export const AssignedGroupsWidget = ({
  menuOptions = [],
  vehicle_id,
  vehicle_group,
  params = {},
}) => {
  const { orgType } = useContext(UserContext);
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useDispatch();
  const isOwner = orgType === 'msOwnerOrg';

  const toggleModal = () => {
    setModalOpen((prevState) => !prevState);
  };

  const fetchUpdateVehicleGroup = useSelector((state) => state.vehicles?.fetchUpdateVehicleGroup);
  const loading = useSelector((state) => state.vehicles?.loading);

  const onEditGroup = (vehicle_group_id) =>
    dispatch(vehicleActions.updateVehicleGroup(vehicle_id, vehicle_group_id));

  const refetch = fetchUpdateVehicleGroup === FETCH_STATE.DONE;

  useEffect(() => {
    if (refetch) {
      dispatch(vehicleActions.fetchVehicleById(vehicle_id));
      setModalOpen(false);
    }
  }, [refetch, dispatch, vehicle_id]);

  return (
    <>
      {modalOpen && (
        <EditAssignedGroupModal
          params={params}
          open={modalOpen}
          isOwner={isOwner}
          onCancel={toggleModal}
          onConfirm={onEditGroup}
          vehicleGroupId={vehicle_group?.id}
        />
      )}
      <Card
        menuItems={menuOptions}
        title={VehiclesGroupsLabel({ plural: true })}
        headerActionType="vertical"
        loading={loading}
        data-cy="vehicle-group-widget"
      >
        <Box data-cy="vehicle-group-widget-container">
          <StyledListItem key={vehicle_group.id}>
            <StyledListItemText
              primary={vehicle_group.name}
              secondary={
                isOwner || vehicle_group.parent ? (
                  <Tooltip title={i18n.t('common.edit')}>
                    <IconButton size="small" aria-haspopup="true" onClick={toggleModal}>
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                ) : null
              }
            />
          </StyledListItem>
          {vehicle_group.parent ? (
            <StyledListItem key={`vg-${vehicle_group.id}`}>
              <StyledListItemText primary={vehicle_group.parent.name} />
            </StyledListItem>
          ) : null}
        </Box>
      </Card>
    </>
  );
};

AssignedGroupsWidget.propTypes = {
  menuOptions: PropTypes.array,
  vehicle_id: PropTypes.string,
  vehicle_group: PropTypes.object,
  params: PropTypes.object,
};

export default AssignedGroupsWidget;
